import React, { useState, useEffect, useMemo } from 'react';
import { EVENT_TYPES } from '../../constants/eventTypes';
import { getCityFromZip } from '../../constants/zipData';
import { distanceMiles, isKnownZip } from '../../utils/distance';
import { fmtDate, fmtTime } from '../../utils/formatting';

// ─── Upcoming Events (public calendar) ──────────────────────────────────────
function UpcomingMarketsPage({ opps, acceptedBookings, setTab, setShowAuthModal, setShowEventGoerSignup, eventGoerProfile, authUser, vendorProfile, isAdmin }) {
  // Full details are shown to: event guests, vendors, hosts (anyone logged in), and admin.
  // Anonymous browsers see a teaser with a "View Event Details" prompt to sign up as a guest.
  const canSeeFullDetails = !!(eventGoerProfile || vendorProfile || authUser || isAdmin);
  const promptSignup = () => { if (setShowEventGoerSignup) setShowEventGoerSignup(true); };
  const [filterType, setFilterType] = useState("");
  const [filterDateFrom, setFilterDateFrom] = useState("");
  const [filterDateTo, setFilterDateTo] = useState("");
  const [filterTicketed, setFilterTicketed] = useState("");
  const [myZip, setMyZip] = useState("");
  const [myRadius, setMyRadius] = useState(0);
  const [expandedId, setExpandedId] = useState(null);
  const [savedEvents, setSavedEvents] = useState(() => { try { return JSON.parse(localStorage.getItem('sjvm_saved_events')||'[]'); } catch { return []; } });
  const [filterSaved, setFilterSaved] = useState('');
  const toggleSave = (id) => { setSavedEvents(s => { const next = s.includes(id) ? s.filter(x=>x!==id) : [...s,id]; localStorage.setItem('sjvm_saved_events',JSON.stringify(next)); return next; }); };
  const shareEvent = (opp) => { const url = window.location.origin; const text = `${opp.eventName} — ${fmtDate(opp.date)} in ${getCityFromZip(opp.zip)||'South Jersey'}`; if (navigator.share) navigator.share({title:opp.eventName,text,url}).catch(err=>console.error('Share failed:',err)); else { navigator.clipboard.writeText(`${text}\n${url}`).then(()=>alert('Event link copied!')).catch(err=>{console.error('Clipboard copy failed:',err);alert('Could not copy to clipboard. Please copy the link manually.');}); } };
  const todayStr = new Date().toISOString().split('T')[0];
  const zipOk = myZip.length===5 && isKnownZip(myZip);
  const upcoming = opps
    .filter(o => o.date >= todayStr)
    .filter(o => o.shareWithEventGoers !== false)
    .filter(o => !filterType || (o.eventTypes || [o.eventType]).includes(filterType))
    .filter(o => !filterDateFrom || o.date >= filterDateFrom)
    .filter(o => !filterDateTo || o.date <= filterDateTo)
    .filter(o => !filterTicketed || (filterTicketed==='yes' ? o.isTicketed : !o.isTicketed))
    .map(o => ({ ...o, dist: zipOk ? distanceMiles(myZip, o.zip) : null }))
    .filter(o => !myRadius || !zipOk || o.dist === null || o.dist <= myRadius)
    .sort((a,b) => { if (a.dist!==null && b.dist!==null) return a.dist - b.dist; return a.date.localeCompare(b.date); });
  const filteredUpcoming = filterSaved === 'saved' ? upcoming.filter(o => savedEvents.includes(o.id)) : upcoming;

  // Derive confirmed vendors from centralized acceptedBookings prop
  const vendorsByEvent = useMemo(() => {
    const byEvent = {};
    upcoming.forEach(o => { byEvent[o.id] = []; });
    (acceptedBookings || []).forEach(v => {
      const opp = upcoming.find(o => o.eventName === v.event_name && o.date === v.event_date);
      if (opp && !byEvent[opp.id].some(existing => existing.vendor_name === v.vendor_name)) {
        byEvent[opp.id].push(v);
      }
    });
    return byEvent;
  }, [acceptedBookings, upcoming]);

  return (
    <>
      <div style={{ background:"linear-gradient(135deg,#1a1410,#2d2118)", padding:"48px 40px", textAlign:"center", position:"relative", overflow:"hidden" }}>
        <div style={{ position:"absolute", inset:0, background:"radial-gradient(ellipse 60% 80% at 50% 50%,rgba(232,201,122,.08),transparent)" }} />
        <h2 style={{ fontFamily:"Playfair Display,serif", fontSize:36, color:"#fff", marginBottom:8, position:"relative" }}>
          Upcoming <em style={{ color:"#e8c97a", fontStyle:"italic" }}>Markets & Events</em>
        </h2>
        <p style={{ color:"#a89a8a", fontSize:16, maxWidth:520, margin:"0 auto", position:"relative" }}>
          Browse upcoming vendor markets, pop-ups, and events across South Jersey.
        </p>
      </div>
      <div className="section" style={{ maxWidth:1100, paddingTop:40 }}>
        <div className="match-filters">
          <div className="match-filter-group" style={{maxWidth:200}}>
            <label>My Zip Code</label>
            <input placeholder="e.g. 08033" value={myZip} maxLength={5} onChange={e=>setMyZip(e.target.value.replace(/\D/g,'').slice(0,5))} />
            {myZip.length===5 && <div className={`zip-feedback ${zipOk?'zip-ok':'zip-warn'}`}>{zipOk ? (myRadius ? `✓ Within ${myRadius} miles` : '✓ Sorted by distance') : '⚠ Zip unverified'}</div>}
          </div>
          {zipOk && (
            <div className="match-filter-group" style={{maxWidth:160}}>
              <label>Radius</label>
              <select value={myRadius} onChange={e=>setMyRadius(+e.target.value)}>
                <option value={0}>Any distance</option>
                <option value={5}>5 miles</option>
                <option value={10}>10 miles</option>
                <option value={15}>15 miles</option>
                <option value={20}>20 miles</option>
                <option value={30}>30 miles</option>
                <option value={50}>50 miles</option>
              </select>
            </div>
          )}
          <div className="match-filter-group">
            <label>Event Type</label>
            <select value={filterType} onChange={e=>setFilterType(e.target.value)}>
              <option value="">All Types</option>
              {EVENT_TYPES.map(t=><option key={t}>{t}</option>)}
            </select>
          </div>
          <div className="match-filter-group">
            <label>From Date</label>
            <input type="date" value={filterDateFrom} min={todayStr} onChange={e=>setFilterDateFrom(e.target.value)} />
          </div>
          <div className="match-filter-group">
            <label>To Date</label>
            <input type="date" value={filterDateTo} min={filterDateFrom||todayStr} onChange={e=>setFilterDateTo(e.target.value)} />
          </div>
          <div className="match-filter-group">
            <label>Ticketed</label>
            <select value={filterTicketed} onChange={e=>setFilterTicketed(e.target.value)}>
              <option value="">All Events</option>
              <option value="yes">Ticketed Only</option>
              <option value="no">Free Only</option>
            </select>
          </div>
          {savedEvents.length > 0 && (
            <div className="match-filter-group" style={{maxWidth:160}}>
              <label>Saved</label>
              <select value={filterSaved} onChange={e=>setFilterSaved(e.target.value)}>
                <option value="">All Events</option>
                <option value="saved">My Saved Only ({savedEvents.length})</option>
              </select>
            </div>
          )}
        </div>
        <div className="results-header">
          <div className="results-count"><strong>{filteredUpcoming.length}</strong> upcoming events{filterSaved==='saved' ? ' (saved)' : ''}</div>
        </div>
        {filteredUpcoming.length===0
          ? <div className="empty-state"><div className="big">📭</div><p>No upcoming events match your filters.</p></div>
          : (
          <div style={{ display:"flex", flexDirection:"column", gap:20 }}>
            {filteredUpcoming.map(opp => {
              const isOpen = expandedId === opp.id;
              const vendors = vendorsByEvent[opp.id] || [];
              return (
              <div key={opp.id} style={{ background:"#fff", border:"1px solid #e8ddd0", borderRadius:12, overflow:"hidden" }}>
                {/* Event photo */}
                {canSeeFullDetails && opp.photoUrl && (
                  <div style={{width:'100%',height:200,overflow:'hidden'}}>
                    <img src={opp.photoUrl} alt={opp.eventName} style={{width:'100%',height:'100%',objectFit:'cover',display:'block'}} />
                  </div>
                )}
                {/* Card header */}
                <div style={{ background:"linear-gradient(135deg,#1a1410,#2d2118)", padding:"18px 24px" }}>
                  <div style={{ fontFamily:"Playfair Display,serif", fontSize:22, color:"#fff", marginBottom:2, lineHeight:1.3 }}>{opp.eventName}</div>
                  {(opp.orgName || opp.contactName) && <div style={{ fontSize:13, color:"#cdb98a", marginBottom:2 }}>Hosted by {opp.orgName || opp.contactName}</div>}
                  <div style={{ fontSize:12, color:"#a89a8a", letterSpacing:"1px", textTransform:"uppercase" }}>{(opp.eventTypes || [opp.eventType]).join(' · ')}</div>
                  {opp.vendorStatus === 'fully_booked' && <span style={{display:'inline-block',marginTop:6,background:'#e8c97a',color:'#1a1410',padding:'3px 12px',borderRadius:12,fontSize:11,fontWeight:700}}>Fully Booked</span>}
                  {opp.source === 'Recurring Series' && <span style={{display:'inline-block',marginTop:6,marginLeft:4,background:'#e8f4fd',color:'#1a4a6b',padding:'3px 12px',borderRadius:12,fontSize:11,fontWeight:700}}>🔄 Recurring Series</span>}
                </div>
                {/* Teaser — date and distance always visible */}
                <div style={{ padding:"16px 24px" }}>
                  <div style={{display:'flex',flexWrap:'wrap',gap:'12px 24px',marginBottom:canSeeFullDetails ? 12 : 0}}>
                    <div><span style={{fontSize:10,textTransform:'uppercase',letterSpacing:1,color:'#a89a8a',fontWeight:600}}>Date </span><span style={{fontSize:14,fontWeight:500}}>{fmtDate(opp.date)}</span></div>
                    {canSeeFullDetails && (opp.startTime || opp.endTime) && <div><span style={{fontSize:10,textTransform:'uppercase',letterSpacing:1,color:'#a89a8a',fontWeight:600}}>Time </span><span style={{fontSize:14,fontWeight:500}}>{fmtTime(opp.startTime)}{opp.endTime ? ' – '+fmtTime(opp.endTime) : ''}</span></div>}
                    <div><span style={{fontSize:10,textTransform:'uppercase',letterSpacing:1,color:'#a89a8a',fontWeight:600}}>{canSeeFullDetails ? 'Location ' : 'Area '}</span><span style={{fontSize:14,fontWeight:500}}>{canSeeFullDetails ? (getCityFromZip(opp.zip) || 'Zip '+opp.zip) : (getCityFromZip(opp.zip) || 'South Jersey')}{opp.dist!==null ? ` · ${opp.dist.toFixed(1)} mi away` : ''}</span></div>
                    {canSeeFullDetails && opp.isTicketed && <div><span style={{fontSize:10,textTransform:'uppercase',letterSpacing:1,color:'#a89a8a',fontWeight:600}}>Admission </span><span style={{fontSize:14,fontWeight:500}}>🎟️ {opp.ticketPrice || 'Ticketed'}</span></div>}
                    {canSeeFullDetails && !opp.isTicketed && <div><span style={{fontSize:10,textTransform:'uppercase',letterSpacing:1,color:'#a89a8a',fontWeight:600}}>Admission </span><span style={{fontSize:14,fontWeight:500,color:'#1a6b3a'}}>Free</span></div>}
                  </div>
                  {canSeeFullDetails && opp.eventGoerNotes && <p style={{fontSize:13,color:'#7a6a5a',lineHeight:1.5,margin:'0 0 12px',padding:'10px 12px',background:'#fdf9f5',borderRadius:6,borderLeft:'3px solid #e8c97a'}}>{opp.eventGoerNotes}</p>}
                  {canSeeFullDetails && opp.eventLink && <div style={{marginBottom:12}}><a href={opp.eventLink} target="_blank" rel="noopener noreferrer" style={{fontSize:12,color:'#1a4a6b',textDecoration:'none',display:'inline-flex',alignItems:'center',gap:4}}>🔗 Event Page</a></div>}
                </div>
                {/* Vendors attending — only when full details visible */}
                {canSeeFullDetails && (
                <div style={{borderTop:'1px solid #e8ddd0',padding:'16px 24px',background:'#fdf9f5'}}>
                  <div style={{fontSize:12,fontWeight:700,color:'#1a1410',letterSpacing:0.5,marginBottom:8}}>Vendors Attending</div>
                  {vendors.length === 0 ? (
                    <div style={{color:'#7a6a5a',fontSize:13,padding:'4px 0'}}>No confirmed vendors yet. Check back closer to the event date!</div>
                  ) : (
                    <div style={{display:'flex',flexWrap:'wrap',gap:8}}>
                      {vendors.map((v,i) => (
                        <div key={i} style={{background:'#fff',border:'1px solid #e8ddd0',borderRadius:8,padding:'8px 12px',display:'flex',alignItems:'center',gap:8}}>
                          {v.vendor_emoji && <span style={{fontSize:16}}>{v.vendor_emoji}</span>}
                          <div>
                            <div style={{fontWeight:700,fontSize:12,color:'#1a1410'}}>{v.vendor_name}</div>
                            {v.vendor_category && <div style={{fontSize:10,color:'#7a6a5a'}}>{v.vendor_category}</div>}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                )}
                {/* Save & Share — full view; or View Event Details CTA for non-guests */}
                {canSeeFullDetails ? (
                  <div style={{borderTop:'1px solid #e8ddd0',padding:'10px 24px',display:'flex',gap:8}}>
                    <button onClick={()=>toggleSave(opp.id)} style={{flex:1,background:savedEvents.includes(opp.id)?'#fdf4dc':'#f5f0ea',color:savedEvents.includes(opp.id)?'#7a5a10':'#7a6a5a',border:'1px solid #e8ddd0',borderRadius:6,padding:'8px',fontSize:12,fontWeight:600,cursor:'pointer',fontFamily:'DM Sans,sans-serif'}}>{savedEvents.includes(opp.id)?'♥ Saved':'♡ Save'}</button>
                    <button onClick={()=>shareEvent(opp)} style={{flex:1,background:'#f5f0ea',color:'#7a6a5a',border:'1px solid #e8ddd0',borderRadius:6,padding:'8px',fontSize:12,fontWeight:600,cursor:'pointer',fontFamily:'DM Sans,sans-serif'}}>↗ Share</button>
                  </div>
                ) : (
                  <div style={{borderTop:'1px solid #e8ddd0',padding:'14px 24px',background:'#fdf9f5',display:'flex',gap:8,flexWrap:'wrap',alignItems:'center'}}>
                    <button onClick={promptSignup} style={{flex:'2 1 200px',background:'#c8a84b',color:'#1a1410',border:'none',borderRadius:6,padding:'10px 16px',fontSize:13,fontWeight:700,cursor:'pointer',fontFamily:'DM Sans,sans-serif'}}>View Event Details</button>
                    <button onClick={promptSignup} style={{flex:'1 1 100px',background:'#fff',color:'#7a6a5a',border:'1px solid #e8ddd0',borderRadius:6,padding:'10px',fontSize:12,fontWeight:600,cursor:'pointer',fontFamily:'DM Sans,sans-serif'}}>♡ Save</button>
                    <button onClick={()=>shareEvent(opp)} style={{flex:'1 1 100px',background:'#fff',color:'#7a6a5a',border:'1px solid #e8ddd0',borderRadius:6,padding:'10px',fontSize:12,fontWeight:600,cursor:'pointer',fontFamily:'DM Sans,sans-serif'}}>↗ Share</button>
                    <div style={{flexBasis:'100%',fontSize:11,color:'#a89a8a',textAlign:'center',marginTop:2}}>Free signup — get personalized event alerts and unlock full details</div>
                  </div>
                )}
              </div>
              );
            })}
          </div>
        )}
        {/* CTAs */}
        <div style={{ marginTop:48, display:'flex', gap:20, justifyContent:'center', flexWrap:'wrap' }}>
          <div style={{ background:'#fff', border:'1px solid #e8ddd0', borderRadius:12, padding:'28px 32px', textAlign:'center', flex:'1 1 280px', maxWidth:380 }}>
            <div style={{ fontSize:28, marginBottom:8 }}>🛍️</div>
            <div style={{ fontFamily:'Playfair Display,serif', fontSize:18, fontWeight:700, marginBottom:6 }}>Are you a vendor?</div>
            <p style={{ fontSize:13, color:'#7a6a5a', marginBottom:14 }}>Find events near you and grow your business.</p>
            <button onClick={()=>{setShowAuthModal ? setShowAuthModal(true) : setTab('vendor');}} className="btn-submit" style={{ fontSize:14, padding:'10px 28px' }}>Apply to Vend</button>
          </div>
          <div style={{ background:'#fff', border:'1px solid #e8ddd0', borderRadius:12, padding:'28px 32px', textAlign:'center', flex:'1 1 280px', maxWidth:380 }}>
            <div style={{ fontSize:28, marginBottom:8 }}>🎪</div>
            <div style={{ fontFamily:'Playfair Display,serif', fontSize:18, fontWeight:700, marginBottom:6 }}>Are you an event organizer?</div>
            <p style={{ fontSize:13, color:'#7a6a5a', marginBottom:14 }}>List your event for free and find quality vendors.</p>
            <button onClick={()=>{setShowAuthModal ? setShowAuthModal(true) : setTab('host');}} className="btn-submit" style={{ fontSize:14, padding:'10px 28px', background:'#e8c97a', color:'#1a1410' }}>List Your Event</button>
          </div>
          <div style={{ background:'#fff', border:'1px solid #e8ddd0', borderRadius:12, padding:'28px 32px', textAlign:'center', flex:'1 1 280px', maxWidth:380 }}>
            <div style={{ fontSize:28, marginBottom:8 }}>📬</div>
            <div style={{ fontFamily:'Playfair Display,serif', fontSize:18, fontWeight:700, marginBottom:6 }}>Love local markets?</div>
            <p style={{ fontSize:13, color:'#7a6a5a', marginBottom:14 }}>Get a personalized list of events near you — free, weekly or biweekly.</p>
            <button onClick={()=>setShowEventGoerSignup(true)} className="btn-submit" style={{ fontSize:14, padding:'10px 28px' }}>Get Event Alerts</button>
          </div>
        </div>
      </div>
    </>
  );
}

export default UpcomingMarketsPage;
